import LegalLayout, { LegalSection } from '@/components/LegalLayout';

export default function TermsPage() {
  return (
    <LegalLayout
      title="Terms of Service"
      subtitle="The agreement between you and Milk for use of the Service. Please read carefully — Section 13 contains a binding arbitration clause and a class-action waiver that affect how disputes are resolved."
      updated="June 2026"
    >
      <LegalSection id="acceptance" title="1. Acceptance of these Terms">
        <p>
          These Terms of Service (the "Terms") govern your access to and use of Milk's conversation-capture
          and follow-up assistant available at addmilk.ai (the "Service"), provided by Milk ("Milk", "we",
          "us", or "our"). By creating an account or using the Service, you agree to be bound by these Terms.
          If you do not agree, do not use the Service.
        </p>
      </LegalSection>

      <LegalSection id="eligibility" title="2. Eligibility &amp; accounts">
        <p>
          You must be of the age of majority in your province or territory to use the Service. You are
          responsible for the activity that occurs under your account and for maintaining the security of
          your sign-in credentials and connected accounts. You agree to provide accurate information and to
          keep it current.
        </p>
      </LegalSection>

      <LegalSection id="service" title="3. The Service">
        <p>
          Milk lets you capture conversations (audio or text), generate transcripts, summaries, decisions,
          and tasks, and — at your direction — send follow-up emails and create calendar reminders from your
          own connected Google or Microsoft account. Features may change, and we may add, modify, or
          discontinue functionality over time.
        </p>
      </LegalSection>

      <LegalSection id="your-content" title="4. Your content">
        <p>
          You retain all rights to the content you capture or create in the Service ("Your Content"). You
          grant Milk a limited licence to host, process, and display Your Content solely to operate and
          provide the Service to you. You are solely responsible for Your Content and for ensuring you have
          the rights and any consents required to capture, record, and process it.
        </p>
      </LegalSection>

      <LegalSection id="recording" title="5. Recording &amp; consent responsibilities">
        <p>
          Laws governing the recording of conversations and meetings vary by jurisdiction and may require the
          consent of one or all participants. You are responsible for understanding and complying with all
          applicable laws when recording, transcribing, or otherwise capturing any conversation using the
          Service, including obtaining any required consent from participants.
        </p>
      </LegalSection>

      <LegalSection id="acceptable-use" title="6. Acceptable use">
        <p>You agree not to:</p>
        <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
          <li>use the Service for any unlawful, infringing, or harmful purpose;</li>
          <li>capture or process content you do not have the right to capture or process;</li>
          <li>attempt to gain unauthorized access to the Service or its systems;</li>
          <li>interfere with or disrupt the integrity or performance of the Service; or</li>
          <li>reverse engineer or misuse the Service except as permitted by law.</li>
        </ul>
      </LegalSection>

      <LegalSection id="third-party" title="7. Third-party services">
        <p>
          The Service integrates with third-party providers (such as Google, Microsoft, and our
          transcription and AI vendors). Your use of those integrations is also subject to the respective
          third party's terms and privacy policies. We are not responsible for third-party services.
        </p>
      </LegalSection>

      <LegalSection id="ip" title="8. Intellectual property">
        <p>
          The Service, including its software, design, and trademarks, is owned by Milk and protected by
          applicable laws. Except for the rights expressly granted to you in these Terms, we reserve all
          rights in and to the Service.
        </p>
      </LegalSection>

      <LegalSection id="fees" title="9. Fees">
        <p>
          Certain features may require a paid subscription. Where fees apply, they will be disclosed to you
          before purchase. Unless otherwise stated, fees are non-refundable except as required by law.
        </p>
      </LegalSection>

      <LegalSection id="disclaimer" title="10. Disclaimers">
        <p>
          The Service is provided "as is" and "as available". To the maximum extent permitted by law, Milk
          disclaims all warranties, express or implied, including merchantability, fitness for a particular
          purpose, and non-infringement. AI-generated transcripts, summaries, and drafts may contain errors;
          you are responsible for reviewing outputs before relying on or sending them.
        </p>
      </LegalSection>

      <LegalSection id="liability" title="11. Limitation of liability">
        <p>
          To the maximum extent permitted by law, Milk and its suppliers will not be liable for any indirect,
          incidental, special, consequential, or punitive damages, or for loss of profits, data, or goodwill,
          arising from or related to your use of the Service. Our total aggregate liability for any claim will
          not exceed the greater of the amounts you paid to Milk in the twelve (12) months preceding the
          claim, or CAD $100. Nothing in these Terms limits liability that cannot be limited under applicable
          law.
        </p>
      </LegalSection>

      <LegalSection id="termination" title="12. Suspension &amp; termination">
        <p>
          You may stop using the Service and delete your account at any time. We may suspend or terminate your
          access if you breach these Terms or if we are required to do so by law. Upon termination, your right
          to use the Service ends, and we will handle Your Content in accordance with our Privacy Policy.
        </p>
      </LegalSection>

      <LegalSection id="arbitration" title="13. Dispute resolution &amp; binding arbitration">
        <p>
          <strong>Please read this section carefully — it affects your legal rights.</strong>
        </p>
        <p>
          <strong>13.1 Informal resolution.</strong> Before commencing any formal proceeding, you agree to
          first contact us at <a href="mailto:legal@addmilk.ai" style={{ color: '#4C73A6' }}>legal@addmilk.ai</a> and
          attempt in good faith to resolve any dispute informally for at least thirty (30) days.
        </p>
        <p>
          <strong>13.2 Binding arbitration.</strong> Except as set out below, any dispute, controversy, or
          claim arising out of or relating to these Terms or the Service that is not resolved informally will
          be finally resolved by confidential, binding arbitration administered by the ADR Institute of Canada
          under its Arbitration Rules. The arbitration will be conducted by a single arbitrator, seated in
          Toronto, Ontario, and conducted in English. Judgment on the award may be entered in any court of
          competent jurisdiction.
        </p>
        <p>
          <strong>13.3 Class-action waiver.</strong> To the fullest extent permitted by law, disputes will be
          resolved on an individual basis only, and you and Milk each waive any right to participate in a class,
          collective, or representative proceeding.
        </p>
        <p>
          <strong>13.4 Exceptions.</strong> Either party may bring a claim in small-claims court, or seek
          injunctive or equitable relief in a court of competent jurisdiction to protect its intellectual
          property or confidential information. This section does not deprive you of any non-waivable right to
          access a court or regulator under applicable consumer-protection law in your jurisdiction.
        </p>
      </LegalSection>

      <LegalSection id="governing-law" title="14. Governing law">
        <p>
          These Terms are governed by the laws of the Province of Ontario and the federal laws of Canada
          applicable therein, without regard to conflict-of-laws principles. Subject to Section 13, the courts
          located in Toronto, Ontario have exclusive jurisdiction, except where applicable law entitles you to
          bring proceedings in your local jurisdiction.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="15. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be posted on this page with an
          updated effective date, and your continued use of the Service after the changes take effect
          constitutes acceptance of the revised Terms.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="16. Contact us">
        <p>
          Milk — Legal<br />
          Email: <a href="mailto:legal@addmilk.ai" style={{ color: '#4C73A6' }}>legal@addmilk.ai</a>
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
